/**
 * Copyright (C) 2023 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React, { useContext } from 'react';
import CircleRoundedIcon from '@mui/icons-material/CircleRounded';
import { Box, List, ListItem, ListItemIcon, ListItemText, SvgIconProps, SxProps } from '@mui/material';

import { useDispatch } from 'react-redux';
import { selectPerformanceOutlookLegendPanelShown } from '../../../store';
import { MapActionsContext, useSelectorWithMapId } from '../map/BaseMapContainer';
import appTheme from '../theme/appTheme.json';
import OverlaysWrapper from './OverlaysWrapper';
import FlightTakeoffRoundedIcon from '@mui/icons-material/FlightTakeoffRounded';
import FlightLandRoundedIcon from '@mui/icons-material/FlightLandRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded';

const PERFORMANCE_OUTLOOK_LEGEND_PANEL_TOP_PX = 27;
const serviceIconSharedProps = { fontSize: '16px' } as SxProps;
const serviceAlertTypeIconSharedProps = { color: '#ffffff' } as SxProps;

export function PerformanceOutlookLegendPanel() {
  const dispatch = useDispatch();
  const mapActionsContext = useContext(MapActionsContext);
  const { hidePerformanceOutlookLegendPanel } = mapActionsContext.mapActions;
  const performanceOutlookLegendPanelShown = useSelectorWithMapId(selectPerformanceOutlookLegendPanelShown);

  return (
    <OverlaysWrapper
      headerTitle="Legend"
      queryParamsToNotHidePanel={['#performance_outlook_legend_panel_button_container']}
      shown={performanceOutlookLegendPanelShown}
      hidePanel={() => {
        dispatch(hidePerformanceOutlookLegendPanel());
      }}
      top={`${PERFORMANCE_OUTLOOK_LEGEND_PANEL_TOP_PX}px`}
      width="220px"
    >
      <Box>
        <List sx={{ width: '224px' }}>
          <PerformanceOutlookLegendPanelListItem
            icon={
              <CircleRoundedIcon sx={{ ...serviceIconSharedProps, color: appTheme.mptNominalServiceFlightPathColor }} />
            }
            labelText="Normal Service"
          />
          <PerformanceOutlookLegendPanelListItem
            icon={<CircleRoundedIcon sx={{ ...serviceIconSharedProps, color: appTheme.mptMarginalServiceColor }} />}
            labelText="Marginal Service"
          />
          <PerformanceOutlookLegendPanelListItem
            icon={
              <CircleRoundedIcon sx={{ ...serviceIconSharedProps, color: appTheme.mptHnaAllAltitudeFlightPathColor }} />
            }
            labelText="HNA (All Airspace)"
          />
          <PerformanceOutlookLegendPanelListItem
            icon={
              <CircleRoundedIcon
                sx={{ ...serviceIconSharedProps, color: appTheme.mptHnaAltitudeRestrictedFlightPathColor }}
              />
            }
            labelText="HNA (Altitude Restricted)"
          />
          <PerformanceOutlookLegendPanelListItem
            icon={
              <CircleRoundedIcon sx={{ ...serviceIconSharedProps, color: appTheme.mptNoCoverageFlightPathColor }} />
            }
            labelText="No Coverage"
          />
          <Box sx={{ height: '16px' }} />
          <PerformanceOutlookLegendPanelListItem
            icon={<FlightTakeoffRoundedIcon sx={{ ...serviceAlertTypeIconSharedProps }} />}
            labelText="Takeoff"
          />
          <PerformanceOutlookLegendPanelListItem
            icon={<SwapHorizRoundedIcon sx={{ ...serviceAlertTypeIconSharedProps }} />}
            labelText="Handoff"
          />
          <PerformanceOutlookLegendPanelListItem
            icon={<FlightLandRoundedIcon sx={{ ...serviceAlertTypeIconSharedProps }} />}
            labelText="Landing"
          />
          <PerformanceOutlookLegendPanelListItem
            icon={<WarningRoundedIcon sx={{ ...serviceAlertTypeIconSharedProps }} />}
            labelText="Service Alert"
          />
        </List>
      </Box>
    </OverlaysWrapper>
  );
}

export default PerformanceOutlookLegendPanel;
interface PerformanceOutlookLegendPanelListItemProps {
  icon: SvgIconProps;
  labelText: string;
}

const PerformanceOutlookLegendPanelListItem = ({ icon, labelText }: PerformanceOutlookLegendPanelListItemProps) => {
  return (
    <ListItem dense sx={{ paddingY: 0 }}>
      <ListItemIcon sx={{ minWidth: 0, marginRight: '8px' }}>{icon}</ListItemIcon>
      <ListItemText sx={{ weight: 400, lineHeight: '24px' }}>{labelText}</ListItemText>
    </ListItem>
  );
};
